import { useState } from "react";

import { parseDetail } from "../utils/errors";
import CopyableGuid from "../components/CopyableGuid";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addTenant, activeTenant } = useTenant();
  const { post } = useRequests();

  const [tenantForm, setTenantForm] = useState({ name: "", slug: "" });
  const [tenantError, setTenantError] = useState("");
  const [tenantSuccess, setTenantSuccess] = useState("");

  const handleCreateTenant = async () => {
    setTenantError("");
    setTenantSuccess("");
    const { data, status } = await post("/admin/tenants", null, tenantForm);
    if (status === 201) {
      addTenant(data);
      setTenantSuccess(`Tenant "${data.name}" created.`);
      setTenantForm({ name: "", slug: "" });
    } else {
      setTenantError(parseDetail(data?.detail, "Failed to create tenant"));
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("settings.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("settings.description")}
      </Typography>

      <Card variant="outlined" sx={{ maxWidth: 600, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("settings.profile.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("settings.profile.description")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Email: {user?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Name: {user?.first_name} {user?.last_name}
          </Typography>
          {user?.sub && <CopyableGuid label="Subject" value={user.sub} />}
          <Typography variant="body2" color="text.secondary">
            Workspace: {activeTenant?.name}
          </Typography>
          {activeTenant?.guid && <CopyableGuid label="Workspace GUID" value={activeTenant.guid} />}
        </CardContent>
      </Card>

      {user?.is_platform_admin && (
        <Card variant="outlined" sx={{ maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("settings.createWorkspace.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("settings.createWorkspace.description")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {tenantError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTenantError("")}>
                {tenantError}
              </Alert>
            )}
            {tenantSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTenantSuccess("")}>
                {tenantSuccess}
              </Alert>
            )}
            <TextField
              label="Name"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              value={tenantForm.name}
              onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
            />
            <TextField
              label="Slug"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              value={tenantForm.slug}
              onChange={(e) => setTenantForm({ ...tenantForm, slug: e.target.value })}
            />
            <Button
              variant="contained"
              onClick={handleCreateTenant}
              disabled={!tenantForm.name || !tenantForm.slug}
            >
              Create Workspace
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
